import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../../../../components/navbar';
import Footer from '../../../../components/footer';
import Button from '../../../../components/Button';
import Checkbox from '../../../../components/Checkbox';
import Dropdown from '../../../../components/Dropdown';
import { useConsultation } from '../../../../context/ConsultationContext';
import { handleTreatmentServiceNavigation } from '../components/treatmentNavigationHelper';
import baseStyles from './css/BloodPressure.module.css';
import styles from './css/UrineDipstick.module.css';
import { FlaskConical, Send } from 'lucide-react';

const UrineDipstick = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { consultationData, updateConsultationData, getStepDraft, setStepDraft, goToStep } = useConsultation();

    const patient = location?.state?.patient || consultationData?.patient || {};
    const patientName = `${patient?.firstname || ""} ${patient?.surname || ""}`.trim() || "Thandiwe Ndlovu";
    const profileNumber = patient?.profilenumber || "448896";
    const initials = `${patient?.firstname?.[0] || ""}${patient?.surname?.[0] || ""}`.toUpperCase() || "TN";

    // Test results states
    const [protein, setProtein] = useState('');
    const [blood, setBlood] = useState('');
    const [ketones, setKetones] = useState('');
    const [specificGravity, setSpecificGravity] = useState('');
    const [urobilinogen, setUrobilinogen] = useState('');
    const [leucocytes, setLeucocytes] = useState('');
    const [ph, setPh] = useState('');
    const [bilirubin, setBilirubin] = useState('');
    
    // Other states
    const [clinicalNotes, setClinicalNotes] = useState('');
    const [utiHistory, setUtiHistory] = useState(false);

    const [symptoms, setSymptoms] = useState({
        burningSensation: false,
        increasedFrequency: false,
        urgency: false,
        abdominalPain: false
    });

    // Dropdown options
    const ketonesOptions = ['Negative', 'Trace', '+1', '+2', '+3', '+4'];
    const specificGravityOptions = ['1.000', '1.005', '1.010', '1.015', '1.020', '1.025', '1.030', '1.035', '1.040'];
    const urobilinogenOptions = ['Normal', '0.1', '0.2', '0.5', '1.0', '2.0', '4.0', '8.0'];
    const leucocytesOptions = ['Negative', 'Trace', '+1', '+2', '+3', '+4'];
    const phOptions = ['5.0', '5.5', '6.0', '6.5', '7.0', '7.5', '8.0', '8.5', '9.0'];
    const bilirubinOptions = ['Negative', '+1', '+2', '+3', '+4'];

    const handleSymptomChange = (symptom) => {
        setSymptoms(prev => ({
            ...prev,
            [symptom]: !prev[symptom]
        }));
    };

    const handleInvoice = () => {
        handleTreatmentServiceNavigation({
            location,
            navigate,
            consultationData,
            updateConsultationData,
            getStepDraft,
            setStepDraft,
            goToStep,
            serviceData: {
                urine_dipstick: {
                    protein,
                    blood,
                    ketones,
                    specificGravity,
                    urobilinogen,
                    leucocytes,
                    ph,
                    bilirubin,
                    clinicalNotes,
                },
            },
        });
    };

    useEffect(() => {
        document.body.classList.add(baseStyles.hideSidebar);
        return () => {
            document.body.classList.remove(baseStyles.hideSidebar);
        };
    }, []);

    return (
        <div className={baseStyles.bloodPressureContainer}>
            <Navbar />

            <main className={baseStyles.mainContent}>
                <div className={baseStyles.card}>
                    <div className={baseStyles.cardHeader}>
                        <div className={baseStyles.headerIcon} aria-hidden="true">
                            <FlaskConical />
                        </div>
                        <h1 className={styles.title}>Urine Dipstick Test</h1>
                    </div>

                    <div className={baseStyles.patientCard}>
                        <div className={baseStyles.avatar} aria-hidden="true">{initials}</div>
                        <div className={baseStyles.patientText}>
                            <div className={baseStyles.patientName}>{patientName}</div>
                            <div className={baseStyles.patientMetaRow}>
                                <span className={baseStyles.profileInfo}>Profile: {profileNumber}</span>
                                <span className={baseStyles.dependentBadge}>Dependent</span>
                            </div>
                        </div>
                    </div>

                    <div className={baseStyles.sectionBlock}>
                        <div className={baseStyles.sectionTitle}>TEST RESULTS</div>
                        <div className={baseStyles.readingsRow}>
                            <div className={baseStyles.readingItem}>
                                <label className={baseStyles.readingLabel}>Protein</label>
                                <div className={baseStyles.inputGroup}>
                                    <input
                                        type="text"
                                        value={protein}
                                        onChange={(e) => setProtein(e.target.value)}
                                        className={baseStyles.readingInput}
                                    />
                                </div>
                            </div>

                            <div className={baseStyles.readingItem}>
                                <label className={baseStyles.readingLabel}>Blood</label>
                                <div className={baseStyles.inputGroup}>
                                    <input
                                        type="text"
                                        value={blood}
                                        onChange={(e) => setBlood(e.target.value)}
                                        className={baseStyles.readingInput}
                                    />
                                </div>
                            </div>

                            <div className={baseStyles.readingItem}>
                                <label className={baseStyles.readingLabel}>Ketones</label>
                                <Dropdown
                                    name="ketones"
                                    value={ketones}
                                    onChange={setKetones}
                                    options={ketonesOptions}
                                    placeholder="Select result"
                                    className={styles.readingInput}
                                />
                            </div>

                            <div className={baseStyles.readingItem}>
                                <label className={baseStyles.readingLabel}>Specific Gravity</label>
                                <Dropdown
                                    name="specificGravity"
                                    value={specificGravity}
                                    onChange={setSpecificGravity}
                                    options={specificGravityOptions}
                                    placeholder="Select result"
                                    className={styles.readingInput}
                                />
                            </div>

                            <div className={baseStyles.readingItem}>
                                <label className={baseStyles.readingLabel}>Urobilinogen</label>
                                <Dropdown
                                    name="urobilinogen"
                                    value={urobilinogen}
                                    onChange={setUrobilinogen}
                                    options={urobilinogenOptions}
                                    placeholder="Select result"
                                    className={styles.readingInput}
                                />
                            </div>

                            <div className={baseStyles.readingItem}>
                                <label className={baseStyles.readingLabel}>Leucocytes</label>
                                <Dropdown
                                    name="leucocytes"
                                    value={leucocytes}
                                    onChange={setLeucocytes}
                                    options={leucocytesOptions}
                                    placeholder="Select result"
                                    className={styles.readingInput}
                                />
                            </div>

                            <div className={baseStyles.readingItem}>
                                <label className={baseStyles.readingLabel}>pH</label>
                                <Dropdown
                                    name="ph"
                                    value={ph}
                                    onChange={setPh}
                                    options={phOptions}
                                    placeholder="Select result"
                                    className={styles.readingInput}
                                />
                            </div>

                            <div className={baseStyles.readingItem}>
                                <label className={baseStyles.readingLabel}>Bilirubin</label>
                                <Dropdown
                                    name="bilirubin"
                                    value={bilirubin}
                                    onChange={setBilirubin}
                                    options={bilirubinOptions}
                                    placeholder="Select result"
                                    className={styles.readingInput}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={baseStyles.sectionBlock}>
                        <div className={baseStyles.sectionTitle}>SYMPTOMS &amp; HISTORY</div>

                        <div className={baseStyles.symptomsList}>
                            <div className={baseStyles.checkboxItem}>
                                <Checkbox
                                    id="burningSensation"
                                    checked={symptoms.burningSensation}
                                    onChange={() => handleSymptomChange('burningSensation')}
                                />
                                <label htmlFor="burningSensation" className={baseStyles.checkboxLabel}>Burning sensation</label>
                            </div>

                            <div className={baseStyles.checkboxItem}>
                                <Checkbox
                                    id="increasedFrequency"
                                    checked={symptoms.increasedFrequency}
                                    onChange={() => handleSymptomChange('increasedFrequency')}
                                />
                                <label htmlFor="increasedFrequency" className={baseStyles.checkboxLabel}>Increased frequency</label>
                            </div>

                            <div className={baseStyles.checkboxItem}>
                                <Checkbox
                                    id="urgency"
                                    checked={symptoms.urgency}
                                    onChange={() => handleSymptomChange('urgency')}
                                />
                                <label htmlFor="urgency" className={baseStyles.checkboxLabel}>Urgency</label>
                            </div>
                            
                            <div className={baseStyles.checkboxItem}>
                                <Checkbox
                                    id="abdominalPain"
                                    checked={symptoms.abdominalPain}
                                    onChange={() => handleSymptomChange('abdominalPain')}
                                />
                                <label htmlFor="abdominalPain" className={baseStyles.checkboxLabel}>Lower abdominal pain</label>
                            </div>
                        </div>
                    </div>

                    <div className={baseStyles.sectionBlock}>
                        <div className={baseStyles.fieldLabel}>Clinical Notes</div>
                        <textarea
                            value={clinicalNotes}
                            onChange={(e) => setClinicalNotes(e.target.value)}
                            className={baseStyles.clinicalNotes}
                            placeholder="Enter clinical notes..."
                            rows={6}
                        />
                    </div>

                    <div className={baseStyles.cardFooter}>
                        <Button
                            onClick={handleInvoice}
                            className={baseStyles.invoiceButton}
                            type="button"
                        >
                            <Send size={16} />
                            Invoice
                        </Button>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default UrineDipstick;